import { ReactNode } from 'react';
import { QueryClient, QueryClientProvider as TanstackQueryClientProvider } from '@tanstack/react-query';

// Create a client with default configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time: 5 minutes (matches backend cache)
      staleTime: 5 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff, except for client errors
      retry: (failureCount, error: unknown) => {
        const apiError = error as { status?: number };
        // Don't retry client errors (400-499) as they indicate invalid input/auth
        if (apiError?.status && apiError.status >= 400 && apiError.status < 500) {
          return false;
        }
        // Retry server errors and network errors up to 3 times
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (good for stale data)
      refetchOnWindowFocus: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
    },
  },
});

interface QueryClientProviderProps {
  children: ReactNode;
}

/**
 * React Query provider for the application
 * Wraps the app to provide query caching and data fetching capabilities
 */
export function QueryClientProvider({ children }: QueryClientProviderProps) {
  return (
    <TanstackQueryClientProvider client={queryClient}>
      {children}
    </TanstackQueryClientProvider>
  );
}
